import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';
import { MAX_CHECKLIST_ITEMS, type ChecklistItem } from './todo.ts';

/**
 * A checklist somebody keeps rather than retypes (P3-09b).
 *
 * Company-level, like the chart of accounts and the equipment register:
 * "what we always check before a tank goes in the ground" belongs to the
 * company, not to one job. The same reasoning as `scheduleTemplates` —
 * and the maintainer's UST work is the case for it, since the same
 * fifteen rows get typed onto every removal.
 *
 * **Rows are text, and nothing else.** A `ChecklistItem` carries `done`,
 * `doneAt`, `doneBy` and `assigneeIds`; a template carries none of them.
 * Freezing an assignee onto a template would put last year's foreman on
 * next year's job, and a template that remembered something as done is a
 * template that lies the moment it is applied. Applying one builds fresh
 * rows with fresh ids.
 *
 * The same cap as a live checklist, deliberately: a template that could
 * hold more rows than a to-do can is a template that fails on apply, and
 * the honest place to refuse is where somebody is typing.
 */
export const ChecklistTemplateRowSchema = z.object({
  id: IdSchema,
  text: z.string().trim().min(1).max(500),
});
export type ChecklistTemplateRow = z.infer<typeof ChecklistTemplateRowSchema>;

export const ChecklistTemplateSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().trim().min(1).max(200),
  /** What it is for, so a library of twenty is navigable. */
  description: z.string().trim().max(1000).default(''),
  rows: z.array(ChecklistTemplateRowSchema).max(MAX_CHECKLIST_ITEMS).default([]),
  /**
   * Retired rather than deleted, like a cost code.
   *
   * A template somebody applied last month is part of how that job was
   * run. Deleting it would not change the to-do it produced — the rows
   * are copied — but it would remove the only record of where those rows
   * came from.
   */
  active: z.boolean().default(true),
});
export type ChecklistTemplate = z.infer<typeof ChecklistTemplateSchema>;

/**
 * The rows a template would add to a checklist, as fresh items.
 *
 * Pure, so the preview and the write cannot disagree — the same reason
 * `shiftedBy` was extracted for the assistant's proposals. `newId` is
 * passed in because ids come from the client in this product and a
 * function that minted its own would make every call unrepeatable.
 */
export function rowsFrom(
  template: Pick<ChecklistTemplate, 'rows'>,
  newId: () => string,
): ChecklistItem[] {
  return template.rows.map((row) => ({
    id: newId(),
    text: row.text,
    // Never carried from the template. A row that arrived already ticked
    // would be a check nobody performed.
    done: false,
    // Nor an assignee: freezing one onto a template would put last year's
    // foreman on next year's job.
    assigneeIds: [],
  }));
}

/** Whether applying this template would overflow an existing checklist. */
export function wouldOverflow(
  existingRows: number,
  template: Pick<ChecklistTemplate, 'rows'>,
): boolean {
  return existingRows + template.rows.length > MAX_CHECKLIST_ITEMS;
}
